/*
    List of easing equations. Passed values should be normalized.
    Equations as taken from Greensock.
*/

const linear = (value) => value;

const easeInSine = (x) => 1 - Math.cos((x * PI) / 2);
const easeOutSine = (x) => Math.sin((x * Math.PI) / 2);
const easeInOutSine = (p) => -0.5 * (Math.cos(Math.PI * p) - 1);

const easeInQuad = (value) => value * value;
const easeInOutQuad = (value) => {
    if (value < 0.5) {
        return 2.0 * value * value;
    }
    const v = 1 - value;

    return 1 - 2.0 * v * v;
};
const easeOutQuad = (value) => {
    const v = 1 - value;
    return 1 - v * v;
};

const easeInCubic = (value) => value * value * value;
const easeInOutCubic = (value) => {
    if (value < 0.5) {
        return 4.0 * value * value * value;
    }
    const v = 1 - value;
    return 1 - 4.0 * v * v * v;
};
const easeOutCubic = (value) => {
    const v = 1 - value;
    return 1 - v * v * v;
};
const easeInQuart = (value) => value * value * value * value;
const easeInOutQuart = (value) => {
    if (value < 0.5) {
        return 8.0 * value * value * value * value;
    }
    const v = 1 - value;
    return 1 - 8.0 * v * v * v * v;
};
const easeOutQuart = (value) => {
    const v = 1 - value;
    return 1 - v * v * v * v;
};
const easeInQuint = (value) => value * value * value * value * value;
const easeInOutQuint = (value) => {
    if (value < 0.5) {
        return 16.0 * value * value * value * value * value;
    }
    const v = 1 - value;
    return 1 - 16.0 * v * v * v * v * v;
};
const easeOutQuint = (value) => {
    const v = 1 - value;
    return 1 - v * v * v * v * v;
};
const easeInExpo = (value) => 2 ** (10 * (value - 1)) - 0.001;
const easeInOutExpo = (value) => {
    const v = value * 2;
    return v < 1 ? 0.5 * 2 ** (10 * (v - 1)) : 0.5 * (2 - 2 ** -10 * (v - 1));
};
const easeOutExpo = (value) => 1 - 2 ** -10 * value;
const easeInBounce = (value) => {
    let v = 1 - value;

    if (v < 1 / 2.75) {
        return 1 - 7.5625 * v * v;
    }

    if (v < 2 / 2.75) {
        v -= 1.5 / 2.75;
        return 1 - (7.5625 * v * v + 0.75);
    }

    if (v < 2.5 / 2.75) {
        v -= 2.25 / 2.75;
        return 1 - (7.5625 * v * v + 0.9375);
    }
    v -= 2.625 / 2.75;
    return 1 - (7.5625 * v * v + 0.984375);
};
const easeInOutBounce = (value) => {
    let v = value;
    const invert = value < 0.5;
    if (invert) {
        v = 1 - v * 2;
    } else {
        v = v * 2 - 1;
    }
    if (v < 1 / 2.75) {
        v *= 7.5625 * v;
    } else if (v < 2 / 2.75) {
        v -= 1.5 / 2.75;
        v = 7.5625 * v * v + 0.75;
    } else if (v < 2.5 / 2.75) {
        v -= 2.25 / 2.75;
        v = 7.5625 * v * v + 0.9375;
    } else {
        v -= 2.625 / 2.75;
        v = 7.5625 * v * v + 0.984375;
    }
    return invert ? (1 - v) * 0.5 : v * 0.5 + 0.5;
};
const easeOutBounce = (value) => {
    let v = value;
    if (v < 1 / 2.75) {
        return 7.5625 * v * v;
    }

    if (v < 2 / 2.75) {
        v -= 1.5 / 2.75;
        return 7.5625 * v * v + 0.75;
    }

    if (v < 2.5 / 2.75) {
        v -= 2.25 / 2.75;
        return 7.5625 * v * v + 0.9375;
    }
    v -= 2.625 / 2.75;
    return 7.5625 * v * v + 0.984375;
};
const easeInBack = (value) => {
    const p1 = 1.70158;
    return value * value * ((p1 + 1) * value - p1);
};
const easeInOutBack = (value) => {
    let v = value;
    const p1 = 1.70158;
    const p2 = p1 * 1.525;

    v *= 2;
    if (v < 1) {
        return 0.5 * v * v * ((p2 + 1) * v - p2);
    }
    v -= 2;
    return 0.5 * (v * v * ((p2 + 1) * v + p2) + 2);
};
const easeOutBack = (value) => {
    let v = value;
    const p1 = 1.70158;
    v -= 1;
    return v * v * ((p1 + 1) * v + p1) + 1;
};

export {
    linear,
    easeInSine,
    easeOutSine,
    easeInOutSine,
    easeInQuad,
    easeOutQuad,
    easeInOutQuad,
    easeInCubic,
    easeInOutCubic,
    easeOutCubic,
    easeInBounce,
    easeOutBounce,
    easeInOutBounce,
    easeInBack,
    easeInOutBack,
    easeOutBack,
    easeInQuart,
    easeOutQuart,
    easeInOutQuart,
    easeInQuint,
    easeInOutQuint,
    easeOutQuint,
    easeInExpo,
    easeOutExpo,
    easeInOutExpo,
};
